import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthBg from "./AuthBg";
import * as Yup from "yup";
import { useFormik } from "formik";
import { apiHeader, postData } from "../../helpers/ApiHelper";
import ClipLoader from "react-spinners/ClipLoader";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { ToastContainer } from "react-toastify";
import { toasterrormsg, toastsuccessmsg, encryptState, decryptState, encrypt } from "../../helpers/Reusable";
import { auth } from "../../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// Validation Schemas
const step1Schema = Yup.object().shape({
  fullName: Yup.string()
    .trim()
    .required("Full name is required")
    .min(2, "Full name must be at least 2 characters"),
  email: Yup.string()
    .trim()
    .required("Email is required")
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      "Email is not valid",
    ),
  password: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()?"])[A-Za-z\d!@#$%^&*()?"]{12,}$/,
      "Password must be at least 12 characters with uppercase, lowercase, number, and special character",
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

const step2Schema = Yup.object().shape({
  mobileNumber: Yup.string()
    .trim()
    .required("Mobile number is required")
    .min(10, "Mobile number is not valid"),
  emailOtp: Yup.string()
    .required("Email OTP is required")
    .length(6, "OTP must be 6 digits"),
  mobileOtp: Yup.string()
    .required("Mobile OTP is required")
    .length(6, "OTP must be 6 digits"),
  agreeTerms: Yup.boolean().oneOf([true], "You must agree to terms"),
});

// Helper function to safely clear reCAPTCHA
const clearRecaptcha = () => {
  try {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }
  } catch (e) {
    // Ignore errors during cleanup
  }
  window.recaptchaVerifier = null;

  // Remove any leftover reCAPTCHA iframes
  const recaptchaElements = document.querySelectorAll(
    'iframe[src*="recaptcha"]',
  );
  recaptchaElements.forEach((el) => {
    try {
      el.parentNode?.removeChild(el);
    } catch (e) {}
  });

  // Clear the container content
  const container = document.getElementById("recaptcha-container");
  if (container) {
    container.innerHTML = "";
  }
};

// Encrypted key for sessionStorage
const REGISTRATION_KEY = encrypt("registrationData");

// Helper function to get saved registration data from sessionStorage (encrypted)
const getSavedRegistrationData = () => {
  try {
    const saved = sessionStorage.getItem(REGISTRATION_KEY);
    return saved ? decryptState(saved) : null;
  } catch {
    return null;
  }
};

const Register = () => {
  const savedData = getSavedRegistrationData();

  const [currentStep, setCurrentStep] = useState(savedData?.currentStep || 1);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState({ email: false, mobile: false });
  const [otpSent, setOtpSent] = useState(
    savedData?.otpSent || { email: false, mobile: false },
  );
  const [otpVerified, setOtpVerified] = useState(
    savedData?.otpVerified || {
      email: false,
      mobile: false,
    },
  );
  const [otpTimer, setOtpTimer] = useState({ email: 0, mobile: 0 });
  const [isPasswordShow, setIsPasswordShow] = useState(false);
  const [isConfirmPasswordShow, setIsConfirmPasswordShow] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isRegistered, setIsRegistered] = useState(
    savedData?.isRegistered || false,
  );
  const [fromLogin, setFromLogin] = useState(savedData?.fromLogin || false);
  const [phoneValue, setPhoneValue] = useState(savedData?.phoneValue || "");
  const [countryCode, setCountryCode] = useState(
    savedData?.countryCode || "91",
  );
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [formData, setFormData] = useState(
    savedData?.formData || {
      fullName: "",
      email: "",
      mobileNumber: "",
      fullPhoneNumber: "",
      password: "",
      confirmPassword: "",
      referCode: "",
      emailOtp: "",
      userId: "",
      mobileOtp: "",
      agreeTerms: false,
    },
  );
  const navigate = useNavigate();
  const recaptchaInitialized = useRef(false);

  // Initialize reCAPTCHA - only once when entering Step 2
  const initializeRecaptcha = useCallback(() => {
    // Prevent multiple initializations
    if (recaptchaInitialized.current || window.recaptchaVerifier) {
      setRecaptchaReady(true);
      return;
    }

    const container = document.getElementById("recaptcha-container");
    if (!container) {
      console.error("reCAPTCHA container not found");
      return;
    }

    // Clear container first
    container.innerHTML = "";

    // Create a new div inside the container for reCAPTCHA
    const recaptchaDiv = document.createElement("div");
    recaptchaDiv.id = "recaptcha-widget";
    container.appendChild(recaptchaDiv);

    try {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-widget",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA verified successfully");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired, clearing...");
            clearRecaptcha();
            setRecaptchaReady(false);
            recaptchaInitialized.current = false;
          },
          "error-callback": () => {
            console.log("reCAPTCHA error, clearing...");
            clearRecaptcha();
            setRecaptchaReady(false);
            recaptchaInitialized.current = false;
          },
        },
      );

      // Render the reCAPTCHA
      window.recaptchaVerifier
        .render()
        .then(() => {
          console.log("reCAPTCHA rendered successfully");
          recaptchaInitialized.current = true;
          setRecaptchaReady(true);
        })
        .catch((error) => {
          console.error("reCAPTCHA render error:", error);
          clearRecaptcha();
          recaptchaInitialized.current = false;
          setRecaptchaReady(false);
        });
    } catch (error) {
      console.error("reCAPTCHA initialization error:", error);
      clearRecaptcha();
      recaptchaInitialized.current = false;
      setRecaptchaReady(false);
    }
  }, []);

  // Initialize reCAPTCHA when on Step 2
  useEffect(() => {
    if (currentStep === 2) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeRecaptcha();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, initializeRecaptcha]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      clearRecaptcha();
      recaptchaInitialized.current = false;
    };
  }, []);

  // OTP Timer
  useEffect(() => {
    const interval = setInterval(() => {
      setOtpTimer((prev) => ({
        email: prev.email > 0 ? prev.email - 1 : 0,
        mobile: prev.mobile > 0 ? prev.mobile - 1 : 0,
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Save registration state to sessionStorage (encrypted)
  useEffect(() => {
    const dataToSave = {
      currentStep,
      isRegistered,
      fromLogin,
      formData,
      phoneValue,
      countryCode,
      otpSent,
      otpVerified,
    };
    sessionStorage.setItem(REGISTRATION_KEY, encryptState(dataToSave));
  }, [
    currentStep,
    isRegistered,
    fromLogin,
    formData,
    phoneValue,
    countryCode,
    otpSent,
    otpVerified,
  ]);

  // Clear sessionStorage on successful registration
  const clearRegistrationData = () => {
    sessionStorage.removeItem(REGISTRATION_KEY);
  };

  // Step 1 form
  const step1Formik = useFormik({
    initialValues: {
      fullName: formData.fullName,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      referCode: formData.referCode,
    },
    validationSchema: step1Schema,
    onSubmit: async (values) => {
      // If already registered, just move to step 2 without calling API again
      if (isRegistered) {
        setFormData((prev) => ({ ...prev, ...values }));
        setCurrentStep(2);
        return;
      }

      setLoading(true);

      // Call registration API
      const param = {
        fullName: values.fullName,
        email: values.email?.toLowerCase(),
        password: values.password,
        ...(values.referCode && { referCode: values.referCode }),
      };

      const response = await postData("user/register", param, apiHeader(false));
      console.log(response, "response");

      if (response?.status == "200" && response?.data?.status == "200") {
        toastsuccessmsg(response.data.message || "Registration successful");

        const userId =
          response?.data?.data?.userId || response?.data?.userId || "";
        console.log(userId, "userId");

        setFormData((prev) => ({ ...prev, ...values, userId }));
        setIsRegistered(true);
        setCurrentStep(2);
      } else {
        toasterrormsg(response?.data?.message || "Registration failed");
      }

      setLoading(false);
    },
  });

  const handlePhoneChange = (value, data) => {
    setPhoneValue(value);
    setCountryCode(data.dialCode);
    const phoneWithoutCode = value.slice(data.dialCode.length);
    step2Formik.setFieldValue("mobileNumber", phoneWithoutCode);
    setFormData((prev) => ({ ...prev, mobileNumber: phoneWithoutCode }));
  };

  // Custom handler to sync step2 form values with formData for sessionStorage
  const handleStep2Change = (e) => {
    const { id, value, type, checked } = e.target;
    step2Formik.handleChange(e);
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  // Step 2 form
  const step2Formik = useFormik({
    initialValues: {
      mobileNumber: formData.mobileNumber,
      emailOtp: formData.emailOtp,
      mobileOtp: formData.mobileOtp,
      agreeTerms: formData.agreeTerms,
    },
    enableReinitialize: true,
    validationSchema: step2Schema,
    // onSubmit: async (values) => {
    //   setLoading(true);

    //   // Use local variables to track verification status (since setState is async)
    //   let isEmailVerified = otpVerified.email;
    //   let isMobileVerified = otpVerified.mobile;

    //   if (!isEmailVerified) {
    //     const emailRes = await postData(
    //       "user/email/otp/verify",
    //       { email: formData.email, otp: values.emailOtp },
    //       apiHeader(false),
    //     );
    //     if (
    //       String(emailRes?.status == "200") &&
    //       String(emailRes?.data?.status) == "200"
    //     ) {
    //       isEmailVerified = true;
    //       setOtpVerified((prev) => ({ ...prev, email: true }));
    //     } else {
    //       toasterrormsg(emailRes.data.message);
    //       setLoading(false);
    //       return;
    //     }
    //   }

    //   if (!isMobileVerified) {
    //     if (!confirmationResult) {
    //       toasterrormsg("Please send mobile OTP first");
    //       setLoading(false);
    //       return;
    //     }
    //     try {
    //       await confirmationResult.confirm(values.mobileOtp);
    //       isMobileVerified = true;
    //       setOtpVerified((prev) => ({ ...prev, mobile: true }));
    //     } catch (error) {
    //       toasterrormsg("Invalid mobile OTP");
    //       setLoading(false);
    //       return;
    //     }
    //   }

    //   // Save mobile number
    //   const mobileParam = {
    //     userId: formData.userId,
    //     countryCode: countryCode,
    //     mobileNumber: values.mobileNumber,
    //     mobileNumberVerify: isMobileVerified,
    //   };
    //   console.log(
    //     "mobileParam:",
    //     mobileParam,
    //     "isMobileVerified:",
    //     isMobileVerified,
    //     "otpVerified.mobile:",
    //     otpVerified.mobile,
    //   );
    //   const mobileResponse = await postData(
    //     "user/save/mobilenumber",
    //     mobileParam,
    //     apiHeader(false),
    //   );
    //   if (
    //     !(
    //       mobileResponse?.status == "200" &&
    //       mobileResponse?.data?.status == "200"
    //     )
    //   ) {
    //     toasterrormsg(
    //       mobileResponse?.data?.message || "Failed to save mobile number",
    //     );
    //     setLoading(false);
    //     return;
    //   }

    //   toastsuccessmsg("Mobile Number and Email Verified Successfully.");
    //   clearRegistrationData();
    //   setTimeout(() => navigate("/login"), 1500);
    //   setLoading(false);
    // },
    onSubmit: async (values) => {
      setLoading(true);

      let isEmailVerified = otpVerified.email;
      let isMobileVerified = otpVerified.mobile;

      // Email OTP verify
      if (!isEmailVerified) {
        const emailRes = await postData(
          "user/email/otp/verify",
          { email: formData.email, otp: values.emailOtp },
          apiHeader(false),
        );
        if (
          String(emailRes?.status == "200") &&
          String(emailRes?.data?.status) == "200"
        ) {
          isEmailVerified = true;
          setOtpVerified((prev) => ({ ...prev, email: true }));
        } else {
          toasterrormsg(emailRes.data.message);
          setLoading(false);
          return;
        }
      }

      // Firebase Mobile OTP confirm
      if (!isMobileVerified) {
        if (!confirmationResult) {
          toasterrormsg("Please send mobile OTP first");
          setLoading(false);
          return;
        }
        try {
          await confirmationResult.confirm(values.mobileOtp);
          isMobileVerified = true;
          setOtpVerified((prev) => ({ ...prev, mobile: true }));
        } catch (error) {
          toasterrormsg("Invalid mobile OTP");
          setLoading(false);
          return;
        }
      }

      // 2nd API CALL — save mobile as verified (mobileNumberVerify: isMobileVerified = true from Firebase)
      const mobileResponse = await postData(
        "user/save/mobilenumber",
        {
          userId: formData.userId,
          countryCode: countryCode,
          mobileNumber: values.mobileNumber,
          mobileNumberVerify: isMobileVerified,
        },
        apiHeader(false),
      );

      if (
        !(
          mobileResponse?.status == "200" &&
          mobileResponse?.data?.status == "200"
        )
      ) {
        toasterrormsg(
          mobileResponse?.data?.message || "Failed to save mobile number",
        );
        setLoading(false);
        return;
      }

      toastsuccessmsg("Mobile Number and Email Verified Successfully.");
      clearRegistrationData();
      setTimeout(() => navigate("/login"), 1500);
      setLoading(false);
    },
  });

  // Send Email Otp
  const handleSendEmailOtp = async () => {
    if (!formData.email) {
      toasterrormsg("Email not found");
      return;
    }
    setOtpLoading((prev) => ({ ...prev, email: true }));
    const response = await postData(
      "user/sendmail",
      { email: formData.email?.toLowerCase() },
      apiHeader(false),
    );
    if (response?.status == "200" && response?.data?.status == "200") {
      toastsuccessmsg("OTP sent to your email");
      setOtpSent((prev) => ({ ...prev, email: true }));
      setOtpTimer((prev) => ({ ...prev, email: 60 }));
    } else {
      toasterrormsg(response?.data?.message || "Failed to send OTP");
    }
    setOtpLoading((prev) => ({ ...prev, email: false }));
  };

  // Send Mobile Otp
  // const handleSendMobileOtp = async () => {
  //   if (!phoneValue) {
  //     toasterrormsg("Mobile number not found");
  //     return;
  //   }

  //   // Check if reCAPTCHA is ready
  //   if (!window.recaptchaVerifier || !recaptchaReady) {
  //     toasterrormsg("Please wait, initializing verification...");
  //     // Try to reinitialize
  //     recaptchaInitialized.current = false;
  //     clearRecaptcha();
  //     setTimeout(() => initializeRecaptcha(), 500);
  //     return;
  //   }

  //   setOtpLoading((prev) => ({ ...prev, mobile: true }));

  //   try {
  //     const phoneNumber = "+" + phoneValue;
  //     const confirmation = await signInWithPhoneNumber(
  //       auth,
  //       phoneNumber,
  //       window.recaptchaVerifier,
  //     );
  //     setConfirmationResult(confirmation);
  //     toastsuccessmsg("OTP sent to your mobile");
  //     setOtpSent((prev) => ({ ...prev, mobile: true }));
  //     setOtpTimer((prev) => ({ ...prev, mobile: 60 }));
  //   } catch (error) {
  //     console.error("Send OTP error:", error);
  //     toasterrormsg(error.message || "Failed to send OTP");

  //     // Reset reCAPTCHA on error for retry
  //     clearRecaptcha();
  //     recaptchaInitialized.current = false;
  //     setRecaptchaReady(false);

  //     // Reinitialize after a delay
  //     setTimeout(() => initializeRecaptcha(), 1000);
  //   }

  //   setOtpLoading((prev) => ({ ...prev, mobile: false }));
  // };

  // Send Mobile Otp
const handleSendMobileOtp = async () => {
  if (!phoneValue) {
    toasterrormsg("Mobile number not found");
    return;
  }

  if (!window.recaptchaVerifier || !recaptchaReady) {
    toasterrormsg("Please wait, initializing verification...");
    recaptchaInitialized.current = false;
    clearRecaptcha();
    setTimeout(() => initializeRecaptcha(), 500);
    return;
  }

  setOtpLoading((prev) => ({ ...prev, mobile: true }));

  try {
    // 1st API CALL — check mobile number in DB (mobileNumberVerify: false)
    const checkResponse = await postData(
      "user/save/mobilenumber",
      {
        userId: formData.userId,
        countryCode: countryCode,
        mobileNumber: step2Formik.values.mobileNumber,
        mobileNumberVerify: false,
      },
      apiHeader(false),
    );

    console.log(checkResponse,"RESPONSE");
    

    if (checkResponse?.data?.message == "Mobile Number is Already Exist") {
      toasterrormsg(checkResponse?.data?.message);
      setOtpLoading((prev) => ({ ...prev, mobile: false }));
      return; // ❌ stop here
    }
    

    // Firebase send OTP (only if DB check passed)
    const phoneNumber = "+" + phoneValue;
    const confirmation = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      window.recaptchaVerifier,
    );
    setConfirmationResult(confirmation);
    toastsuccessmsg("OTP sent to your mobile");
    setOtpSent((prev) => ({ ...prev, mobile: true }));
    setOtpTimer((prev) => ({ ...prev, mobile: 60 }));
  } catch (error) {
    console.error("Send OTP error:", error);
    toasterrormsg(error.message || "Failed to send OTP");
    clearRecaptcha();
    recaptchaInitialized.current = false;
    setRecaptchaReady(false);
    setTimeout(() => initializeRecaptcha(), 1000);
  }

  setOtpLoading((prev) => ({ ...prev, mobile: false }));
};

  const handleBack = () => {
    if (currentStep === 2) {
      setFormData((prev) => ({
        ...prev,
        ...step2Formik.values,
        fullPhoneNumber: phoneValue,
      }));
      setCurrentStep(1);
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-[5rem] relative">
      {/* Line connecting the circles */}
      <div
        className={`absolute top-[35px] left-1/2 -translate-x-1/2 w-[200px] lg:w-[180px] h-[5px] transition-all duration-500 ${currentStep > 1 ? "bg-[#C99930]" : "bg-gray-300"}`}
      />

      {[
        { num: 1, label: "About You" },
        { num: 2, label: "Verify" },
      ].map((step, idx) => (
        <React.Fragment key={step.num}>
          <div
            className={`flex flex-col items-center z-10 ${idx === 0 ? "mr-[50px] lg:mr-[80px]" : "ml-[50px] lg:ml-[80px]"}`}
          >
            <div
              className={`w-[70px] h-[70px] rounded-full flex items-center justify-center text-[2.4rem] font-bold border-[4px] transition-all duration-300 ${currentStep >= step.num ? "bg-[#C99930] border-[#C99930] text-white" : "bg-white border-gray-300 text-gray-400"}`}
            >
              {currentStep > step.num ? "✓" : step.num}
            </div>
            <span
              className={`mt-4 text-[2rem] font-semibold ${currentStep >= step.num ? "text-[#C99930]" : "text-gray-400"}`}
            >
              {step.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      <ToastContainer />
      {/* reCAPTCHA container - always present in DOM */}
      <div
        id="recaptcha-container"
        style={{ position: "fixed", bottom: 0, left: 0, zIndex: -1 }}
      ></div>

      <div className="flex flex-col lg:flex-row lg:justify-end min-h-screen bg-yellow">
        <div className="h-auto lg:h-screen z-[1] relative lg:fixed top-0 left-0 lg:w-[40%]">
          <AuthBg />
        </div>

        <div className="lg:w-[55%] min-h-screen ">
          <div className="w-full h-full flex items-center justify-center py-[5rem] px-[2rem] lg:px-[5rem] xl:px-[7rem]">
            <div className="w-full max-w-[700px]">
              <StepIndicator />

              {/* STEP 1 */}
              {currentStep === 1 && (
                <>
                  <h1 className="text-[2.8rem] font-bold text-[#2C3451] mb-3">
                    About You
                  </h1>
                  <p className="text-[1.4rem] text-gray-500 mb-[3.5rem]">
                    Already a user?{" "}
                    <Link
                      to="/login"
                      className="text-[#2C3451] font-bold hover:underline"
                    >
                      Log in
                    </Link>
                  </p>
                  <form onSubmit={step1Formik.handleSubmit}>
                    <div className="flex flex-col gap-[2.5rem]">
                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          placeholder="Enter your full name"
                          className="w-full auth-input"
                          value={step1Formik.values.fullName}
                          onChange={step1Formik.handleChange}
                        />
                        {step1Formik.touched.fullName &&
                          step1Formik.errors.fullName && (
                            <div className="mt-2 error-msg">
                              {step1Formik.errors.fullName}
                            </div>
                          )}
                      </div>

                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          placeholder="Enter your email"
                          className="w-full auth-input"
                          value={step1Formik.values.email}
                          onChange={step1Formik.handleChange}
                        />
                        {step1Formik.touched.email &&
                          step1Formik.errors.email && (
                            <div className="mt-2 error-msg">
                              {step1Formik.errors.email}
                            </div>
                          )}
                      </div>

                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={isPasswordShow ? "text" : "password"}
                            id="password"
                            placeholder="Enter password"
                            className="w-full auth-input"
                            value={step1Formik.values.password}
                            onChange={step1Formik.handleChange}
                          />
                          {isPasswordShow ? (
                            <FaEyeSlash
                              onClick={() => setIsPasswordShow(false)}
                              className="absolute cursor-pointer top-[2rem] right-[2.5rem] auth-input-eye"
                            />
                          ) : (
                            <FaEye
                              onClick={() => setIsPasswordShow(true)}
                              className="absolute cursor-pointer top-[2rem] right-[2.5rem] auth-input-eye"
                            />
                          )}
                        </div>
                        {step1Formik.touched.password &&
                          step1Formik.errors.password && (
                            <div className="mt-2 error-msg">
                              {step1Formik.errors.password}
                            </div>
                          )}
                      </div>

                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            type={isConfirmPasswordShow ? "text" : "password"}
                            id="confirmPassword"
                            placeholder="Confirm your password"
                            className="w-full auth-input"
                            value={step1Formik.values.confirmPassword}
                            onChange={step1Formik.handleChange}
                          />
                          {isConfirmPasswordShow ? (
                            <FaEyeSlash
                              onClick={() => setIsConfirmPasswordShow(false)}
                              className="absolute cursor-pointer top-[2rem] right-[2.5rem] auth-input-eye"
                            />
                          ) : (
                            <FaEye
                              onClick={() => setIsConfirmPasswordShow(true)}
                              className="absolute cursor-pointer top-[2rem] right-[2.5rem] auth-input-eye"
                            />
                          )}
                        </div>
                        {step1Formik.touched.confirmPassword &&
                          step1Formik.errors.confirmPassword && (
                            <div className="mt-2 error-msg">
                              {step1Formik.errors.confirmPassword}
                            </div>
                          )}
                      </div>

                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4">
                          Refer Code
                        </label>
                        <input
                          type="text"
                          id="referCode"
                          placeholder="Enter refer code"
                          className="w-full auth-input"
                          value={step1Formik.values.referCode}
                          onChange={step1Formik.handleChange}
                        />
                      </div>
                      <p className="mb-5 auth-text-g">
                        {/* Hint: The password should be at least twelve characters
                      long. To make it stronger,use upper and lower case
                      letters, numbers, and symbols like ! " ? $ % ^ & {")"}. */}
                        Hint: The password should be at least twelve characters
                        long. It must have upper and lower case letters,
                        numbers, and symbols like ! " ? $ % ^ & {")"}.
                      </p>
                      <p className="auth-text-g">
                        Your personal data will be used to support your
                        experience throughout this website, to manage access to
                        your account, and for other purposes described in our
                        privacy policy.
                      </p>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#C99930] text-white py-[1.6rem] rounded-[14px] text-[1.5rem] font-bold hover:bg-[#b8882a] transition-all shadow-lg mt-4 flex items-center justify-center gap-3"
                      >
                        {loading ? (
                          <>
                            <span>Processing...</span>
                            <ClipLoader size={24} color="#fff" />
                          </>
                        ) : (
                          "Next"
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {/* STEP 2 */}
              {currentStep === 2 && (
                <>
                  <h1 className="text-[2.8rem] font-bold text-[#2C3451] mb-3">
                    Verify your Details
                  </h1>
                  <p className="text-[1.4rem] text-gray-500 mb-[3.5rem]">
                    We'll send OTP to verify your email and mobile
                  </p>
                  <form onSubmit={step2Formik.handleSubmit}>
                    <div className="flex flex-col gap-[2.5rem]">
                      {/* Email with Send OTP */}
                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Your Email
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            value={formData.email}
                            disabled
                            className="w-full auth-input bg-gray-50 text-gray-700 pr-[150px]"
                          />
                          <button
                            type="button"
                            onClick={handleSendEmailOtp}
                            disabled={otpLoading.email || otpTimer.email > 0}
                            className="absolute right-[8px] top-1/2 -translate-y-1/2 px-[1.5rem] py-[0.8rem] text-[#C99930] font-bold text-[1.2rem] hover:bg-[#FFF8E7] disabled:text-gray-400 transition-all rounded-[10px] whitespace-nowrap"
                          >
                            {otpLoading.email ? (
                              <ClipLoader size={20} color="#C99930" />
                            ) : otpTimer.email > 0 ? (
                              `${otpTimer.email}s`
                            ) : otpSent.email ? (
                              "Resend OTP"
                            ) : (
                              "Send OTP"
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Email OTP */}
                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Enter OTP for Email
                        </label>
                        <input
                          type="text"
                          id="emailOtp"
                          maxLength={6}
                          placeholder="Enter 6-digit OTP"
                          className="w-full auth-input tracking-[1.2rem] text-center font-bold"
                          value={step2Formik.values.emailOtp}
                          onChange={handleStep2Change}
                        />
                        {step2Formik.touched.emailOtp &&
                          step2Formik.errors.emailOtp && (
                            <div className="mt-2 error-msg">
                              {step2Formik.errors.emailOtp}
                            </div>
                          )}
                      </div>

                      {/* Mobile with Send OTP */}
                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Mobile Number
                        </label>
                        <div className="relative">
                          <PhoneInput
                            country={"in"}
                            value={phoneValue}
                            onChange={handlePhoneChange}
                            enableSearch={true}
                            searchPlaceholder="Search country..."
                            inputProps={{
                              id: "mobileNumber",
                              placeholder: "Enter mobile number",
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleSendMobileOtp}
                            disabled={
                              otpLoading.mobile ||
                              otpTimer.mobile > 0 ||
                              !step2Formik.values.mobileNumber
                            }
                            className="absolute right-[8px] top-1/2 -translate-y-1/2 px-[1.5rem] py-[0.8rem] text-[#C99930] font-bold text-[1.2rem] hover:bg-[#FFF8E7] disabled:text-gray-400 transition-all rounded-[10px] whitespace-nowrap z-10"
                          >
                            {otpLoading.mobile ? (
                              <ClipLoader size={20} color="#C99930" />
                            ) : otpTimer.mobile > 0 ? (
                              `${otpTimer.mobile}s`
                            ) : otpSent.mobile ? (
                              "Resend OTP"
                            ) : (
                              "Send OTP"
                            )}
                          </button>
                        </div>
                        {step2Formik.touched.mobileNumber &&
                          step2Formik.errors.mobileNumber && (
                            <div className="mt-2 error-msg">
                              {step2Formik.errors.mobileNumber}
                            </div>
                          )}
                      </div>

                      {/* Mobile OTP */}
                      <div className="flex flex-col">
                        <label className="auth-input-label mb-4 required">
                          Enter OTP for Mobile
                        </label>
                        <input
                          type="text"
                          id="mobileOtp"
                          maxLength={6}
                          placeholder="Enter 6-digit OTP"
                          className="w-full auth-input tracking-[1.2rem] text-center font-bold"
                          value={step2Formik.values.mobileOtp}
                          onChange={handleStep2Change}
                        />
                        {step2Formik.touched.mobileOtp &&
                          step2Formik.errors.mobileOtp && (
                            <div className="mt-2 error-msg">
                              {step2Formik.errors.mobileOtp}
                            </div>
                          )}
                      </div>

                      {/* Terms */}
                      <div className="flex items-center gap-5">
                        <input
                          type="checkbox"
                          id="agreeTerms"
                          checked={step2Formik.values.agreeTerms}
                          onChange={handleStep2Change}
                          className="w-[28px] h-[28px] min-w-[28px] accent-[#C99930] cursor-pointer"
                        />
                        <label
                          htmlFor="agreeTerms"
                          className="text-[1.4rem] text-gray-600 cursor-pointer leading-normal"
                        >
                          I agree to receive communications regarding product
                          updates, services, offers, and other relevant updates.
                        </label>
                      </div>
                      {step2Formik.touched.agreeTerms &&
                        step2Formik.errors.agreeTerms && (
                          <div className="error-msg -mt-4">
                            {step2Formik.errors.agreeTerms}
                          </div>
                        )}

                      <div className="flex gap-6 mt-4">
                        {!fromLogin && (
                          <button
                            type="button"
                            onClick={handleBack}
                            className="flex-1 border-2 border-[#C99930] text-[#C99930] py-[1.6rem] rounded-[14px] text-[1.5rem] font-bold hover:bg-[#FFF8E7] transition-all"
                          >
                            Back
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className={`${fromLogin ? "w-full" : "flex-1"} bg-[#C99930] text-white py-[1.6rem] rounded-[14px] text-[1.5rem] font-bold hover:bg-[#b8882a] transition-all shadow-lg flex items-center justify-center gap-3`}
                        >
                          {loading ? (
                            <>
                              <span>Verifying...</span>
                              <ClipLoader size={24} color="#fff" />
                            </>
                          ) : (
                            "Verify"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px white inset !important;
          box-shadow: 0 0 0 30px white inset !important;
          -webkit-text-fill-color: #000 !important;
        }
        .react-tel-input { width: 100% !important; font-family: inherit !important; }
        .react-tel-input .form-control {
          width: 100% !important; height: 65px !important; font-size: 1.4rem !important;
          border-radius: 14px !important; border: 2px solid #e5e7eb !important;
          padding-left: 100px !important; background: #fff !important;
        }
        .react-tel-input .form-control:focus { border-color: #e5e7eb !important; box-shadow: none !important; outline: none !important; }
        .react-tel-input .form-control:-webkit-autofill,
        .react-tel-input .form-control:-webkit-autofill:hover,
        .react-tel-input .form-control:-webkit-autofill:focus,
        .react-tel-input .form-control:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 60px white inset !important;
          box-shadow: 0 0 0 60px white inset !important;
          -webkit-text-fill-color: #000 !important;
          border: 2px solid #e5e7eb !important;
          outline: none !important;
          background-color: white !important;
          background-image: none !important;
          transition: background-color 5000s ease-in-out 0s !important;
        }
        
        /* NEW: Fix for autofill line/divider */
        .react-tel-input .special-label { display: none !important; }
        .react-tel-input .selected-flag { border-right: none !important; }
        .react-tel-input .flag-dropdown::after { display: none !important; content: none !important; }
        .react-tel-input .form-control { border-left: none !important; }
        .react-tel-input .divider { display: none !important; }
        
        .react-tel-input .flag-dropdown {
          border-radius: 14px 0 0 14px !important; border: 2px solid #e5e7eb !important;
          border-right: none !important; background: #f9fafb !important;
        }
        .react-tel-input .flag-dropdown:hover, .react-tel-input .flag-dropdown:focus { background: #f3f4f6 !important; }
        .react-tel-input .flag-dropdown.open { background: #f3f4f6 !important; border-radius: 14px 0 0 0 !important; }
        .react-tel-input .flag-dropdown.open .selected-flag { background: #f3f4f6 !important; border-radius: 14px 0 0 0 !important; }
        .react-tel-input .selected-flag { width: 90px !important; padding: 0 0 0 20px !important; border-radius: 14px 0 0 14px !important; }
        .react-tel-input .selected-flag:hover, .react-tel-input .selected-flag:focus { background: #f3f4f6 !important; }
        .react-tel-input .flag { transform: scale(1.3) !important; }
        .react-tel-input .arrow { margin-left: 8px !important; border-top: 5px solid #6b7280 !important; border-left: 4px solid transparent !important; border-right: 4px solid transparent !important; }
        .react-tel-input .arrow.up { border-top: none !important; border-bottom: 5px solid #6b7280 !important; }
        .react-tel-input .country-list {
          position: absolute !important; top: 100% !important; left: 0 !important; z-index: 9999 !important;
          width: 400px !important; max-height: 350px !important; 
          overflow-y: auto !important;
          overflow-x: hidden !important;
          background: #fff !important; border: 2px solid #e5e7eb !important; border-radius: 14px !important;
          box-shadow: 0 15px 50px rgba(0,0,0,0.15) !important; margin-top: 8px !important; 
          padding: 0 !important;
          box-sizing: border-box !important;
          scrollbar-gutter: stable !important;
        }
        .react-tel-input .country-list::-webkit-scrollbar { width: 6px !important; }
        .react-tel-input .country-list::-webkit-scrollbar-track { background: transparent !important; margin: 60px 0 10px 0 !important; }
        .react-tel-input .country-list::-webkit-scrollbar-thumb { background: #C99930 !important; border-radius: 10px !important; }
        .react-tel-input .country-list .country { padding-right: 24px !important; }
        .react-tel-input .country-list .search { position: sticky !important; top: 0 !important; background: #fff !important; padding: 15px !important; z-index: 1 !important; border-bottom: 1px solid #e5e7eb !important; }
        .react-tel-input .country-list .search-box { width: 100% !important; height: 50px !important; font-size: 1.3rem !important; padding: 0 18px !important; border: 2px solid #e5e7eb !important; border-radius: 12px !important; outline: none !important; margin: 0 !important; box-sizing: border-box !important; }
        .react-tel-input .country-list .search-box:focus { border-color: #C99930 !important; }
        .react-tel-input .country-list .search-emoji { display: none !important; }
        .react-tel-input .country-list .country { display: flex !important; align-items: center !important; padding: 14px 18px !important; padding-right: 12px !important; font-size: 1.3rem !important; cursor: pointer !important; transition: background 0.2s ease !important; box-sizing: border-box !important; width: 100% !important; }
        .react-tel-input .country-list .country:hover { background: #FFF8E7 !important; }
        .react-tel-input .country-list .country.highlight { background: #C99930 !important; color: #fff !important; }
        .react-tel-input .country-list .country .flag { margin-right: 12px !important; transform: scale(1.2) !important; }
        .react-tel-input .country-list .country .country-name { flex: 1 !important; font-weight: 500 !important; color: #2C3451 !important; }
        .react-tel-input .country-list .country.highlight .country-name { color: #fff !important; }
        .react-tel-input .country-list .country .dial-code { color: #6b7280 !important; font-weight: 600 !important; margin-left: 10px !important; }
        .react-tel-input .country-list .country.highlight .dial-code { color: rgba(255,255,255,0.8) !important; }
        .react-tel-input .country-list .no-entries-message { padding: 20px !important; text-align: center !important; color: #6b7280 !important; font-size: 1.2rem !important; }
        .react-tel-input .country-list .divider { border-bottom: 1px solid #e5e7eb !important; margin: 8px 0 !important; }
        .phone-verify-container .react-tel-input .form-control { background: #f9fafb !important; color: #374151 !important; cursor: default !important; padding-right: 96px !important; }
        .phone-verify-container .react-tel-input .flag-dropdown { background: #f3f4f6 !important; pointer-events: none !important; }
        .phone-verify-container .react-tel-input .arrow { display: none !important; }
        .phone-verify-container .react-tel-input .selected-flag { cursor: default !important; }
      `}</style>
    </>
  );
};;

export default Register;
